import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { deckMeta, hero } from '../../data/deck';
import { DeviceFrame } from '../DeviceFrame';
import { SnakExperience } from '../experience/SnakExperience';
import { FormatToggle } from './FormatToggle';
import { PitchActions } from './PitchActions';
import type { Format } from '../../types/presentation';
interface PitchHeroProps {
  format: Format;
  onFormatChange: (format: Format) => void;
  onPlay: () => void;
  onStoryboard: () => void;
  onConcept: () => void;
  onSignals: () => void;
  onJourney: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/** Opening page: the idea on the left, the live Snak on the right. */
export function PitchHero({
  format,
  onFormatChange,
  onPlay,
  onStoryboard,
  onConcept,
  onSignals,
  onJourney
}: PitchHeroProps) {
  const reduce = useReducedMotion();
  const enter = (delay: number, y = 14) => ({
    initial: reduce ? false : {
      opacity: 0,
      y
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      delay,
      duration: 0.3,
      ease: EASE
    }
  });
  return <section id="opening" aria-label={hero.title} className="relative flex min-h-screen w-full flex-col bg-canvas font-sans" data-id="element-457" data-mp="25hxilg7k296n">
      <div className="flex items-center justify-between gap-4 px-8 pt-6 lg:px-14" data-id="element-458" data-mp="2edna86zljzsh">
        <span className="text-[10px] uppercase tracking-[0.24em] text-navy/35" data-id="element-459" data-mp="29ldh6i36qjlz">{deckMeta.concept}</span>
        <FormatToggle value={format} onChange={onFormatChange} data-id="element-460" data-mp="2oa02d0fhu5l4" />
      </div>

      <main className="flex flex-1 items-center" data-id="element-461" data-mp="2jbykujxpuebr">
        <div className="mx-auto grid w-full max-w-[1440px] items-center gap-14 px-8 py-12 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-20 lg:px-14 lg:py-8 lg:pr-24" data-id="element-462" data-mp="2jb6nb3kk66iu">
          <div className="max-w-[580px]" data-id="element-463" data-mp="2t9cmhdezcyk0">
            <motion.h1 {...enter(0)} className="font-display text-[56px] font-semibold leading-[0.94] tracking-[-0.035em] text-navy sm:text-[76px]" data-id="element-464" data-mp="2tans7irzkgdr">
              {hero.title}
            </motion.h1>
            <motion.p {...enter(0.06)} className="mt-7 max-w-[28ch] font-display text-[21px] font-medium leading-[1.3] text-navy/60" data-id="element-465" data-mp="2y0v2lwc60rxh">
              {hero.lead}
            </motion.p>
            <motion.div {...enter(0.12)} className="mt-12" data-id="element-466" data-mp="37yj5j9jxd5qq">
              <PitchActions onPlay={onPlay} onStoryboard={onStoryboard} onConcept={onConcept} onSignals={onSignals} data-id="element-467" data-mp="3cxvubzucwmvy" />
            </motion.div>
          </div>

          <motion.div {...enter(0.1, 24)} className="flex justify-center lg:justify-end" data-id="element-468" data-mp="3lxsfvslg95ms">
            <div className="flex flex-col items-center gap-5" data-id="element-469" data-mp="3qp0mkgveacw9">
              <span className="text-[10px] uppercase tracking-[0.24em] text-navy/35" data-id="element-470" data-mp="3qrmuihm76uf0">Live prototype</span>
              <DeviceFrame label="Live mobile prototype of the DME phenotyping Snak" sizeClassName="h-[clamp(560px,calc(100vh-248px),840px)] w-auto aspect-[344/688]" data-id="element-471" data-mp="4i2eln7unffy">
                <SnakExperience onOpenStrategy={onConcept} onOpenJourney={onJourney} onOpenStoryboard={onStoryboard} data-id="element-472" data-mp="1t1g8jnwjcbc0" />
              </DeviceFrame>
              <span className="text-[11px] tracking-[0.02em] text-navy/40" data-id="element-473" data-mp="1k08fplcsg2pb">
                Tap to play · Scroll inside the phone
              </span>
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="px-8 pb-6 lg:px-14" data-id="element-474" data-mp="2coe026zxjpom">
        <p className="text-[10px] uppercase tracking-[0.18em] text-navy/25" data-id="element-475" data-mp="27qm8je3765ky">{deckMeta.footer}</p>
      </footer>
    </section>;
}